import React, { useEffect, useRef, useState } from 'react';
import './TicketStartPage.css';

/** Legacy magnifier icon, served from the customer site (matches start.tmpl). */
const SEARCH_ICON_URL = '/ws2k13/assets/icons/icon_magnify.svg';

/** BetterStack announcement widget fetched on mount (matches legacy ticket.js). */
const DEFAULT_ANNOUNCEMENT_URL = 'https://uptime.betterstack.com/widgets/announcement?id=199074';

const STATUS_BADGE_URL = 'https://www.heartstatus.uk/badge';
const STATUS_SITE_URL = 'https://www.heartstatus.uk';
const SUPPORT_SEARCH_URL = '/manage/support.cgi';
const SUPPORT_DB_URL = 'https://www.heartinternet.uk/support/';
const RAISE_TICKET_URL = '/manage/ticket.cgi?action=raise_form';
const TICKET_LIST_URL = '/manage/ticket.cgi/';

/** Animated search-box placeholder suggestions (matches legacy). */
const SEARCH_SUGGESTIONS = [
  'How do I add an SSL?',
  'How do I restore a backup?',
  'How do I update my nameservers?',
  'How do I migrate WordPress?',
];

/** Common-question links (matches legacy). */
const COMMON_QUESTIONS = [
  {
    label: 'How do I set up 2FA on my Heart Internet account?',
    href: 'https://www.heartinternet.uk/support/article/how-do-i-setup-2fa-on-my-heart-internet-account.html',
  },
  {
    label: 'How do I order an SSL Certificate?',
    href: 'https://www.heartinternet.uk/support/article/how-do-i-order-an-ssl-certificate.html',
  },
  {
    label: 'How do I add SPF records to my site?',
    href: 'https://www.heartinternet.uk/support/article/how-do-i-add-spf-records-to-my-site.html',
  },
  {
    label: 'How do I use trackbacks in WordPress?',
    href: 'https://www.heartinternet.uk/support/article/how-do-i-use-trackbacks-in-wordpress.html',
  },
];

// Typewriter timings (ms) — matches legacy ticket.js.
const TYPING_SPEED = 120;
const PAUSE = 1600;
const BACKSPACE_SPEED = 40;

export interface TicketStartPageProps {
  /**
   * BetterStack system-status announcement widget URL, fetched on mount.
   * Its HTML is shown in a banner when present, otherwise nothing is rendered.
   */
  announcementUrl?: string;
  /** Injectable fetch implementation (defaults to global fetch) — for tests. */
  fetchFn?: typeof window.fetch;
}

/**
 * Customer Services landing — /manage/ticket.cgi (default action).
 *
 * The support hub: a system-status card (heartstatus.uk badge + link), a Support
 * Database search form (opens support.cgi in a new tab, with an animated typewriter
 * placeholder), and a contact section (raise / view tickets). A BetterStack
 * announcement banner is fetched on mount. Page chrome (nav, sidebar, footer,
 * breadcrumbs) is provided by the Page organism at mount; the Tawk.to live-chat
 * widget is injected by the template, not this component.
 *
 * Faithful 1:1 port of the legacy start.tmpl — the bespoke design (grey cards,
 * gradient search border, yellow/outline CTAs) has no matching hi-ui variants, so
 * the body uses native elements styled by the scoped TicketStartPage.css.
 */
export const TicketStartPage: React.FC<TicketStartPageProps> = ({
  announcementUrl = DEFAULT_ANNOUNCEMENT_URL,
  fetchFn,
}) => {
  const [announcementHtml, setAnnouncementHtml] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [showCaret, setShowCaret] = useState(false);

  // Typewriter state machine (refs so the recursive timeout reads live values).
  const suggestionIndexRef = useRef(0);
  const charIndexRef = useRef(0);
  const typingRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Fetch the BetterStack announcement; show its HTML or nothing (legacy hides it).
  useEffect(() => {
    let cancelled = false;
    const doFetch = fetchFn || fetch;
    doFetch(announcementUrl)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setAnnouncementHtml(data && data.html ? data.html : null);
      })
      .catch(() => {
        if (!cancelled) setAnnouncementHtml(null);
      });
    return () => {
      cancelled = true;
    };
  }, [announcementUrl, fetchFn]);

  // Animated typewriter placeholder — type, pause, backspace, next; loop.
  useEffect(() => {
    const type = () => {
      const current = SEARCH_SUGGESTIONS[suggestionIndexRef.current];
      if (typingRef.current) {
        if (charIndexRef.current <= current.length) {
          setPlaceholder(current.slice(0, charIndexRef.current));
          setShowCaret(true);
          charIndexRef.current += 1;
          timerRef.current = setTimeout(type, TYPING_SPEED);
        } else {
          typingRef.current = false;
          timerRef.current = setTimeout(type, PAUSE);
        }
      } else if (charIndexRef.current > 0) {
        charIndexRef.current -= 1;
        setPlaceholder(current.slice(0, charIndexRef.current));
        setShowCaret(charIndexRef.current > 0);
        timerRef.current = setTimeout(type, BACKSPACE_SPEED);
      } else {
        typingRef.current = true;
        suggestionIndexRef.current = (suggestionIndexRef.current + 1) % SEARCH_SUGGESTIONS.length;
        timerRef.current = setTimeout(type, TYPING_SPEED);
      }
    };
    type();
    return () => clearTimeout(timerRef.current);
  }, []);

  // Any interaction with the search box stops the animation for good (legacy):
  // cancelling the pending timer halts the loop; clear the overlay.
  const stopTypewriter = () => {
    clearTimeout(timerRef.current);
    setPlaceholder('');
    setShowCaret(false);
  };

  // Support-database links point at heartinternet.uk/support/ (PHPKB), which
  // can't handle the query params GTM's cross-domain linker appends to links at
  // click time. Open them via window.open so PHPKB receives an undecorated URL
  // — the same guard TicketViewPage applies to KB links inside ticket bodies.
  const openSupportLink = (href: string, target: '_blank' | '_self') => (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(href, target);
  };

  return (
    <div className="hi-ui-ticket-start-page">
      <h1 className="hi-ui-ticket-start-page__title">Customer Services</h1>
      <span className="hi-ui-ticket-start-page__intro">
        Get answers fast: search guides or check live system status.
      </span>

      {announcementHtml && (
        <div
          className="hi-ui-ticket-start-page__announcement"
          dangerouslySetInnerHTML={{ __html: announcementHtml }}
        />
      )}

      <div className="hi-ui-ticket-start-page__sections">
        {/* System status */}
        <div className="hi-ui-ticket-start-page__section hi-ui-ticket-start-page__section--status">
          <div className="hi-ui-ticket-start-page__status-row">
            <iframe
              className="hi-ui-ticket-start-page__status-badge"
              src={STATUS_BADGE_URL}
              width="250"
              height="30"
              frameBorder="0"
              scrolling="no"
              title="System status"
            />
            <a
              href={STATUS_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hi-ui-ticket-start-page__cta hi-ui-ticket-start-page__cta--solid"
            >
              View System Status
            </a>
          </div>
        </div>

        {/* Support Database */}
        <div className="hi-ui-ticket-start-page__section">
          <h2 className="hi-ui-ticket-start-page__section-header">
            Instant help with our Support Database
          </h2>
          <span className="hi-ui-ticket-start-page__intro">
            Over 50% of questions are solved in minutes with our step-by-step guides.
          </span>
          <form action={SUPPORT_SEARCH_URL} method="get" target="_blank">
            <input type="hidden" name="action" value="search" />
            <input type="hidden" name="phpkb" value="1" />
            <div className="hi-ui-ticket-start-page__search-border">
              <div className="hi-ui-ticket-start-page__search">
                <input
                  type="text"
                  name="searchtext"
                  id="search-input"
                  className="hi-ui-ticket-start-page__search-input"
                  aria-label="Search the Support Database"
                  autoComplete="off"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    stopTypewriter();
                  }}
                  onFocus={stopTypewriter}
                />
                <span className="hi-ui-ticket-start-page__typewriter" aria-hidden="true">
                  {placeholder}
                  {showCaret && <span className="hi-ui-ticket-start-page__caret" />}
                </span>
                <button type="submit" className="hi-ui-ticket-start-page__search-button">
                  <img src={SEARCH_ICON_URL} alt="Search Icon" />
                </button>
              </div>
            </div>
            <div className="hi-ui-ticket-start-page__questions">
              <span className="hi-ui-ticket-start-page__question-header">Common questions</span>
              {COMMON_QUESTIONS.map((q) => (
                <a
                  key={q.href}
                  href={q.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={openSupportLink(q.href, '_blank')}
                  className="hi-ui-ticket-start-page__question-link"
                >
                  {q.label}
                </a>
              ))}
              <div className="hi-ui-ticket-start-page__button-group">
                <a
                  href={SUPPORT_DB_URL}
                  onClick={openSupportLink(SUPPORT_DB_URL, '_self')}
                  className="hi-ui-ticket-start-page__cta hi-ui-ticket-start-page__cta--outline"
                >
                  Visit support database
                </a>
              </div>
            </div>
          </form>
        </div>

        {/* Contact */}
        <div className="hi-ui-ticket-start-page__section">
          <h2 className="hi-ui-ticket-start-page__section-header">
            Still need help? Contact our Support Team
          </h2>
          <span className="hi-ui-ticket-start-page__intro">
            If your question isn&apos;t answered in the support database and there&apos;s no active
            incident on the system status page, please create a ticket and one of our expert team
            will get back to you.
          </span>
          <div className="hi-ui-ticket-start-page__button-group">
            <a
              href={RAISE_TICKET_URL}
              className="hi-ui-ticket-start-page__cta hi-ui-ticket-start-page__cta--outline"
            >
              Create ticket
            </a>
            <a
              href={TICKET_LIST_URL}
              className="hi-ui-ticket-start-page__cta hi-ui-ticket-start-page__cta--outline"
            >
              View your tickets
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
